import { useState, useEffect } from 'react';
import { X, Save, Loader2, FileText } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { serversApi, ReadFileResponse } from '../../lib/api/servers';

interface FileEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  serverId: string;
  filePath: string;
  readOnly?: boolean;
  onSave?: () => void;
}

export function FileEditorModal({
  isOpen,
  onClose,
  serverId,
  filePath,
  readOnly = false,
  onSave,
}: FileEditorModalProps) {
  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileName = filePath.split('/').pop() || filePath;
  const hasChanges = content !== originalContent;

  useEffect(() => {
    if (isOpen && filePath) {
      loadFile();
    }
  }, [isOpen, filePath, serverId]);

  const loadFile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: ReadFileResponse = await serversApi.readFile(serverId, filePath);
      if (response.error) {
        setError(response.error);
      } else {
        setContent(response.content || '');
        setOriginalContent(response.content || '');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await serversApi.writeFile(serverId, filePath, content);
      if (response.error) {
        setError(response.error);
      } else {
        setOriginalContent(content);
        onSave?.();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="xl">
      <div className="flex flex-col h-[70vh]">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{fileName}</h3>
              <p className="text-sm text-gray-500 font-mono">{filePath}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && hasChanges && (
              <span className="text-sm text-amber-600">Unsaved changes</span>
            )}
            {!readOnly && (
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleClose}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 mt-4 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading file...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-800 rounded-md">
              <p className="font-medium">Error loading file</p>
              <p className="text-sm mt-1">{error}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={loadFile}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              readOnly={readOnly}
              className="w-full h-full p-4 font-mono text-sm bg-gray-900 text-gray-100 rounded-lg border-0 resize-none focus:ring-2 focus:ring-primary-500 focus:outline-none"
              spellCheck={false}
              placeholder="File is empty"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
